import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Link
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { validateEmail, validatePassword, validateName, validateAndTrimInput } from '../utils/validation';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (email: string, password: string, name: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  existingEmails?: string[]; // Array of existing emails to check for duplicates
}

const LoginDialog: React.FC<LoginDialogProps> = ({
  open,
  onClose,
  onLogin,
  onRegister,
  loading,
  error,
  existingEmails = []
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  
  // Validation states
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsValidating(true);

    // Clear previous validation errors
    setEmailError(null);
    setPasswordError(null);
    setNameError(null);

    // Validate and trim email
    const emailValidation = validateAndTrimInput(email, 'Email');
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error || 'Email validation failed');
      setIsValidating(false);
      return;
    }

    // Validate email format and check for duplicates
    const emailFormatValidation = validateEmail(emailValidation.trimmedValue, true, existingEmails);
    if (!emailFormatValidation.isValid) {
      setEmailError(emailFormatValidation.error || 'Invalid email format');
      setIsValidating(false);
      return;
    }

    // Validate and trim password
    const passwordValidation = validateAndTrimInput(password, 'Password');
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.error || 'Password validation failed');
      setIsValidating(false);
      return;
    }

    // Validate password strength
    const passwordStrengthValidation = validatePassword(passwordValidation.trimmedValue);
    if (!passwordStrengthValidation.isValid) {
      setPasswordError(passwordStrengthValidation.error || 'Invalid password format');
      setIsValidating(false);
      return;
    }

    // Validate name for registration
    if (!isLogin) {
      const nameValidation = validateAndTrimInput(name, 'Name');
      if (!nameValidation.isValid) {
        setNameError(nameValidation.error || 'Name validation failed');
        setIsValidating(false);
        return;
      }

      const nameFormatValidation = validateName(nameValidation.trimmedValue);
      if (!nameFormatValidation.isValid) {
        setNameError(nameFormatValidation.error || 'Invalid name format');
        setIsValidating(false);
        return;
      }
    }

    try {
      if (isLogin) {
        await onLogin(email, password);
      } else {
        await onRegister(email, password, name);
      }
      // Clear form on success
      setEmail('');
      setPassword('');
      setName('');
      setIsValidating(false);
    } catch (err) {
      // Error handling is done in parent component
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setName('');
    setFormError(null);
    setEmailError(null);
    setPasswordError(null);
    setNameError(null);
    setIsValidating(false);
    onClose();
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormError(null);
    setEmailError(null);
    setPasswordError(null);
    setNameError(null);
  };

  // Real-time email validation
  useEffect(() => {
    if (email && email.length > 3) {
      const timeoutId = setTimeout(() => {
        // First check if it's not just whitespace
        const emailValidation = validateAndTrimInput(email, 'Email');
        if (!emailValidation.isValid) {
          setEmailError(emailValidation.error || null);
          return;
        }

        // Then validate email format and check for duplicates
        const emailFormatValidation = validateEmail(emailValidation.trimmedValue, true, existingEmails);
        setEmailError(emailFormatValidation.isValid ? null : emailFormatValidation.error || null);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setEmailError(null);
    }
  }, [email, existingEmails]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          {isLogin ? 'Welcome Back' : 'Create Account'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isLogin ? 'Sign in to access your SVG downloads' : 'Join ArtFul to start downloading SVGs'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {(error || formError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || formError}
            </Alert>
          )}

          {!isLogin && (
            <TextField
              fullWidth
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              error={!!nameError}
              helperText={nameError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            error={!!emailError}
            helperText={emailError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
          
          {/* Password Strength Indicator - only show for registration */}
          {!isLogin && password && (
            <PasswordStrengthIndicator password={password} />
          )}

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            error={!!passwordError}
            helperText={passwordError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />

          {isLogin && (
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Link
                href="#"
                variant="body2"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot password?
              </Link>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || isValidating}
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          {loading ? 'Please wait...' : isValidating ? 'Validating...' : (isLogin ? 'Sign In' : 'Create Account')}
        </Button>

        <Divider sx={{ width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          onClick={toggleMode}
          disabled={loading}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          {isLogin ? 'Create new account' : 'Already have an account? Sign in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginDialog; 